import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { 
  Typography, 
  Container, 
  Box, 
  Button, 
  Card, 
  Grid,
  Divider,
  useMediaQuery
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ExploreIcon from "@mui/icons-material/Explore";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTheme } from "@mui/material/styles";

const TourPacksPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const fadeInLeft = {
    hidden: { opacity: 0, x: -50 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };
  
  const fadeInRight = {
    hidden: { opacity: 0, x: 50 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const staggerChildren = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const floatAnimation = {
    animate: {
      y: [0, -15, 0],
      transition: {
        duration: 4,
        repeat: Infinity,
        repeatType: "reverse",
        ease: "easeInOut"
      }
    }
  };

  // Tour packages data
  const tours = [
    {
      id: 1,
      title: "Ellora Caves and City Tour",
      image: "images/elloracaves2.jpg",
      places: [
        "Daultabad Fort",
        "Ellora Caves",
        "Grishneshwar Temple",
        "Khuldabad",
        "Bibi Ka Maqbara",
        "Panchakki",
        "Ajanta Caves"
      ],
      description: "Explore the architectural marvels of Aurangabad's historical sites and UNESCO World Heritage caves.",
      background: "white",
      buttonColor: "error",
      buttonText: "white",
      textColor: "#333",
      titleColor: "#db352a",
      direction: "row",
      icon: <FlightTakeoffIcon />
    },
    {
      id: 2,
      title: "South India Tours",
      image: "images/Hampi.jpg",
      places: [
        "Bijapur Site",
        "Pattadakal",
        "Aihole",
        "Badami",
        "Hampi"
      ],
      description: "Discover the rich cultural heritage and breathtaking landscapes of South India's most celebrated monuments.",
      background: "linear-gradient(135deg, #db352a 0%, #ff7366 100%)",
      buttonColor: "inherit",
      buttonText: "#db352a",
      textColor: "white",
      titleColor: "white",
      direction: "row-reverse",
      icon: <ExploreIcon />
    },
    {
      id: 3,
      title: "Shirdi / Shani Shignapur Tour",
      image: "images/shirdi2.jpeg",
      places: [
        "Shirdi",
        "Shani Shignapur",
        "Nashik City",
        "Paithan City"
      ],
      description: "Experience spiritual rejuvenation with a tour of the most sacred temples and pilgrimage sites.",
      background: "white",
      buttonColor: "error",
      buttonText: "white",
      textColor: "#333",
      titleColor: "#db352a",
      direction: "row",
      icon: <DirectionsBusIcon />
    }
  ];

  return (
    <div style={{ overflow: "hidden" }}>
      {/* Background pattern */}
      <div style={{ 
        position: "fixed", 
        width: "100%", 
        height: "100vh", 
        zIndex: -1,
        backgroundImage: "url('/images/world-map-background.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        opacity: 0.05
      }} />

      <Header />

      {/* Hero Section */}
      <Box
        sx={{
          width: "100%",
          pt: { xs: 8, md: 12 },
          pb: { xs: 6, md: 8 },
          background: "linear-gradient(to bottom, rgba(219,53,42,0.05) 0%, rgba(255,255,255,1) 100%)",
          position: "relative",
          overflow: "hidden"
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Box sx={{ textAlign: "center", mb: 6 }}>
              <Typography
                variant="h2"
                component="h1"
                sx={{ 
                  fontWeight: "bold",
                  color: "#db352a",
                  mb: 2,
                  position: "relative",
                  display: "inline-block",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    width: "60%",
                    height: "4px",
                    backgroundColor: "#db352a",
                    bottom: "-12px",
                    left: "20%"
                  }
                }}
              >
                Tour Packages
              </Typography>
              
              <Typography 
                variant="h6" 
                sx={{ 
                  maxWidth: "800px", 
                  mx: "auto", 
                  mt: 4,
                  color: "#555"
                }}
              >
                Experience the rich heritage and beauty of India with our carefully crafted tour packages. 
                From ancient caves to spiritual journeys, we've got the perfect adventure waiting for you.
              </Typography>
            </Box>
          </motion.div>

          {/* Decorative elements */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.5 }}
            style={{
              position: "absolute",
              right: isMobile ? "-100px" : "5%",
              top: "10%",
              zIndex: 0
            }}
          >
            <motion.img
              animate={{ 
                y: [0, -15, 0],
                rotate: [0, 5, 0]
              }}
              transition={{ 
                duration: 6, 
                repeat: Infinity,
                repeatType: "reverse"
              }}
              src="illustrations/hourglass.png"
              alt="Hourglass"
              style={{
                width: isMobile ? "80px" : "120px",
                height: "auto",
                opacity: 0.8,
                filter: "drop-shadow(0px 5px 10px rgba(0,0,0,0.1))"
              }}
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.7 }}
            style={{
              position: "absolute",
              left: isMobile ? "-120px" : "5%",
              top: "30%",
              zIndex: 0
            }}
          >
            <motion.img
              animate={{ 
                y: [0, 10, 0],
                rotate: [0, -5, 0]
              }}
              transition={{ 
                duration: 5, 
                repeat: Infinity,
                repeatType: "reverse"
              }}
              src="illustrations/pointer with signs in different directions.png"
              alt="Direction signs"
              style={{
                width: isMobile ? "100px" : "150px",
                height: "auto",
                opacity: 0.8,
                filter: "drop-shadow(0px 5px 10px rgba(0,0,0,0.1))"
              }}
            />
          </motion.div>
        </Container>
      </Box>

      {/* Tour Packages */}
      <Box>
        {tours.map((tour, index) => (
          <Box 
            key={tour.id}
            sx={{ 
              background: tour.background,
              position: "relative",
              overflow: "hidden"
            }}
          >
            {/* Add background pattern for colored sections */}
            {tour.background !== "white" && (
              <Box sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                background: "url('/images/world-map-dots.png')",
                backgroundSize: "cover",
                opacity: 0.1,
                zIndex: 0
              }} />
            )}
            
            <Container maxWidth="lg" sx={{ py: 8, position: "relative", zIndex: 1 }}>
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={index % 2 === 0 ? fadeInLeft : fadeInRight}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: {
                      xs: "column",
                      md: tour.direction
                    },
                    alignItems: "center",
                    gap: { xs: 4, md: 6 }
                  }}
                >
                  {/* Image Section */}
                  <motion.div
                    whileHover={{ scale: 1.03 }}
                    transition={{ type: "spring", stiffness: 300 }}
                    style={{ 
                      flex: "0 0 auto", 
                      width: isMobile ? "100%" : "45%",
                      position: "relative"
                    }}
                  >
                    <Box
                      component="img"
                      src={tour.image}
                      alt={tour.title}
                      sx={{
                        width: "100%",
                        aspectRatio: "4/3",
                        objectFit: "cover",
                        borderRadius: "16px",
                        boxShadow: "0 15px 30px rgba(0,0,0,0.15)",
                      }}
                    />
                    
                    {/* Location badge */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: "20px",
                        right: "20px",
                        backgroundColor: "#db352a",
                        color: "white",
                        borderRadius: "30px",
                        px: 2,
                        py: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        zIndex: 2
                      }}
                    >
                      {tour.icon}
                      <Typography variant="button" sx={{ fontWeight: "bold" }}>
                        {tour.places.length} Destinations
                      </Typography>
                    </Box>
                  </motion.div>

                  {/* Content Section */}
                  <Box 
                    sx={{ 
                      flex: 1,
                      p: { xs: 0, md: 3 }
                    }}
                  >
                    <motion.div
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                      variants={staggerChildren}
                    >
                      <motion.div variants={fadeIn}>
                        <Typography
                          variant="h3"
                          component="h2"
                          color={tour.titleColor}
                          sx={{ 
                            fontWeight: "bold",
                            mb: 3, 
                            fontSize: { xs: "2rem", md: "2.5rem" }
                          }}
                        >
                          {tour.title}
                        </Typography>
                      </motion.div>

                      <motion.div variants={fadeIn}>
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            mb: 3, 
                            color: tour.textColor,
                            opacity: 0.9
                          }}
                        >
                          {tour.description}
                        </Typography>
                      </motion.div>

                      <Divider sx={{ 
                        my: 3, 
                        borderColor: tour.background !== "white" ? 
                          "rgba(255,255,255,0.2)" : 
                          "rgba(0,0,0,0.1)" 
                      }} />

                      <Typography 
                        variant="h6" 
                        sx={{ 
                          mb: 2, 
                          color: tour.titleColor,
                          fontWeight: "medium",
                          display: "flex",
                          alignItems: "center",
                          gap: 1
                        }}
                      >
                        <LocationOnIcon /> Destinations
                      </Typography>

                      <Grid container spacing={2} sx={{ mb: 4 }}>
                        {tour.places.map((place, idx) => (
                          <Grid item xs={12} sm={6} key={idx}>
                            <motion.div
                              variants={fadeIn}
                              whileHover={{ x: 5 }}
                              transition={{ type: "spring", stiffness: 300 }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  mb: 1
                                }}
                              >
                                <CheckCircleOutlineIcon 
                                  sx={{ 
                                    mr: 1, 
                                    fontSize: "1.1rem",
                                    color: tour.titleColor
                                  }} 
                                />
                                <Typography 
                                  variant="body1"
                                  sx={{ color: tour.textColor }}
                                >
                                  {place}
                                </Typography>
                              </Box>
                            </motion.div>
                          </Grid>
                        ))}
                      </Grid>

                      <motion.div
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <Button
                          component={Link}
                          to="/booking"
                          variant="contained"
                          color={tour.buttonColor}
                          size="large"
                          sx={{
                            px: 4,
                            py: 1.2,
                            borderRadius: "50px",
                            color: tour.buttonText,
                            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                            fontWeight: "bold",
                            backgroundColor: tour.buttonColor === "inherit" ? "white" : undefined
                          }}
                          startIcon={<ExploreIcon />}
                        >
                          Book This Tour
                        </Button>
                      </motion.div>
                    </motion.div>
                  </Box>
                </Box>
              </motion.div>
            </Container>
          </Box>
        ))}
      </Box>

      {/* Call to Action Section */}
      <Box
        sx={{
          width: "100%",
          py: 10,
          background: "linear-gradient(to right, rgba(219,53,42,0.05) 0%, rgba(219,53,42,0.1) 100%)",
        }}
      >
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Card
              elevation={0}
              sx={{
                p: { xs: 3, md: 5 },
                borderRadius: "20px",
                textAlign: "center",
                boxShadow: "0 15px 50px rgba(0,0,0,0.1)",
                border: "1px solid rgba(219,53,42,0.1)",
                background: "rgba(255,255,255,0.9)",
                backdropFilter: "blur(10px)"
              }}
            >
              <Typography
                variant="h4"
                component="h2"
                color="primary"
                sx={{ 
                  mb: 2, 
                  fontWeight: "bold",
                  color: "#db352a" 
                }}
              >
                Need a Custom Tour Package?
              </Typography>
              
              <Typography 
                variant="body1" 
                sx={{ mb: 4, maxWidth: "600px", mx: "auto" }}
              >
                We can create the perfect itinerary tailored to your interests, schedule, and budget. 
                Contact our travel experts today!
              </Typography>
              
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  component={Link}
                  to="/contact-us"
                  variant="contained"
                  color="error"
                  size="large"
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderRadius: "50px",
                    fontWeight: "bold",
                    boxShadow: "0 4px 12px rgba(219,53,42,0.3)",
                  }}
                >
                  Contact Us
                </Button>
              </motion.div>
            </Card>
          </motion.div>
        </Container>
      </Box>

      <Footer />
    </div>
  );
};

export default TourPacksPage;